// What a cancellation left on a payment, as tags: money captured that the admin
// refunds outside the app, and a hold the payment provider would not release.
// Each tag can take keyboard focus, so its tooltip (when and what to do) opens
// without a mouse, and wraps rather than running off the cell when the text is
// zoomed. Values: docs/design/admin-cancellation-money-tags.md.
import type { ReactNode } from 'react';
import { Space, Tag, Tooltip, theme } from 'antd';

import { CANCELLATION_MARK_COLORS } from '../data/statuses';
import { dateTime } from '../data/format';
import './CancellationMoneyTags.css';

/**
 * Money kept on a cancelled booking. `paidBy`: how it was paid, which is how it
 * goes back - a card payment at the payment provider, a bank transfer by
 * transfer (a charter balance paid by wire; its card deposit, if any, at the
 * provider).
 */
export function RefundOutsideAppTag({ at, paidBy = 'card' }: { at?: string | null; paidBy?: 'card' | 'wire_transfer' }) {
  if (!at) return null;
  const wire = paidBy === 'wire_transfer';
  return (
    <Tooltip
      trigger={['hover', 'focus']}
      title={
        wire
          ? `Marked for refund ${dateTime(at)}. The app does not refund money taken: refund the bank transfer to the customer by bank transfer, and any card deposit at the payment provider.`
          : `Marked for refund ${dateTime(at)}. The app does not refund captured payments: refund it at the payment provider.`
      }
    >
      <Tag tabIndex={0} className="money-mark" color={CANCELLATION_MARK_COLORS.refundOutsideApp}>
        {wire ? 'Received · refund outside the app' : 'Captured · refund outside the app'}
      </Tag>
    </Tooltip>
  );
}

export function ReleaseFailedTag({ at, reason }: { at?: string | null; reason?: string | null }) {
  if (!at) return null;
  return (
    <Tooltip trigger={['hover', 'focus']} title={`${dateTime(at)}: the payment provider did not release the hold, so it may still be on the card. Release it at the provider.`}>
      <Tag tabIndex={0} className="money-mark" color={CANCELLATION_MARK_COLORS.releaseFailed}>
        Release failed{reason ? `: ${reason}` : ''}
      </Tag>
    </Tooltip>
  );
}

/** A payment's status tag and its cancellation marks, on one wrapping row. */
export function PaymentTagRow({ children }: { children: ReactNode }) {
  const { token } = theme.useToken();
  return (
    <Space size={token.marginXXS} wrap>
      {children}
    </Space>
  );
}
